import axiosInstance from '../axiosConfig';
import {
  ConversionApplication,
  ConversionApplicationFormData,
  ConversionApplicationResponse,
  ConversionApplicationsResponse,
  ConversionPreview,
} from '../../types';

/**
 * Conversion Service
 * Handles conversion application and completion API calls
 */
class ConversionService {
  private baseUrl = '/conversion-applications';
  private conversionUrl = '/conversions';

  // ============================================
  // Conversion Applications
  // ============================================

  /**
   * Get all conversion applications with optional filters
   */
  async getAll(params?: {
    status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<ConversionApplicationsResponse> {
    const response = await axiosInstance.get(this.baseUrl, { params });
    return response.data;
  }

  /**
   * Get conversion application by ID
   */
  async getById(id: string): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get my conversion applications (for current user)
   */
  async getMyApplications(): Promise<ConversionApplicationsResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/my`);
    return response.data;
  }

  /**
   * Get applications pending review (staff/admin)
   */
  async getPendingReview(): Promise<ConversionApplicationsResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/pending-review`);
    return response.data;
  }

  /**
   * Get waitlist
   */
  async getWaitlist(): Promise<ConversionApplicationsResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/waitlist`);
    return response.data;
  }

  /**
   * Create a new conversion application
   */
  async create(data: ConversionApplicationFormData): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update a conversion application (draft only)
   */
  async update(
    id: string,
    data: Partial<ConversionApplicationFormData>
  ): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Submit an application for review
   */
  async submit(id: string): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/submit`);
    return response.data;
  }

  /**
   * Withdraw an application
   */
  async withdraw(id: string, reason?: string): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/withdraw`, { reason });
    return response.data;
  }

  // ============================================
  // Staff/Admin Review Actions
  // ============================================

  /**
   * Start review of an application
   */
  async startReview(id: string): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/start-review`);
    return response.data;
  }

  /**
   * Request additional information
   */
  async requestAdditionalInfo(
    id: string,
    notes: string
  ): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/request-info`, { notes });
    return response.data;
  }

  /**
   * Approve an application
   */
  async approve(
    id: string,
    data: {
      initiation_fee?: number;
      first_dues?: number;
      payment_due_date?: string;
      notes?: string;
    }
  ): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/approve`, data);
    return response.data;
  }

  /**
   * Reject an application
   */
  async reject(id: string, reason: string): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/reject`, { reason });
    return response.data;
  }

  /**
   * Add to waitlist
   */
  async addToWaitlist(id: string, position?: number): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/waitlist`, { position });
    return response.data;
  }

  /**
   * Record payment received
   */
  async recordPayment(
    id: string,
    paymentId?: string
  ): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/payment`, { payment_id: paymentId });
    return response.data;
  }

  // ============================================
  // Sponsor Actions
  // ============================================

  /**
   * Approve as sponsor
   */
  async approveSponsor(
    id: string,
    sponsorNumber: 1 | 2
  ): Promise<ConversionApplicationResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${id}/sponsor-approve`, {
      sponsor_number: sponsorNumber,
    });
    return response.data;
  }

  // ============================================
  // Conversion Completion
  // ============================================

  /**
   * Preview conversion (what will happen)
   */
  async previewConversion(applicationId: string): Promise<{ success: boolean; data: ConversionPreview }> {
    const response = await axiosInstance.get(`${this.conversionUrl}/${applicationId}/preview`);
    return response.data;
  }

  /**
   * Complete the conversion
   */
  async completeConversion(
    applicationId: string,
    data?: {
      payment_id?: string;
      custom_member_number?: string;
    }
  ): Promise<{
    success: boolean;
    data: {
      application: ConversionApplication;
      member: {
        id: string;
        member_number: string;
        member_type: string;
        membership_status: string;
        first_name: string;
        last_name: string;
        email: string;
        join_date: string;
      };
      converted_family_members: Array<{
        original_non_member_family_id: number;
        new_family_member_id?: number;
        name?: string;
        member_sub_number?: string;
        success: boolean;
        error?: string;
      }>;
      family_member_count: number;
    };
    message: string;
  }> {
    const response = await axiosInstance.post(`${this.conversionUrl}/${applicationId}/complete`, data);
    return response.data;
  }

  /**
   * Get applications ready for conversion
   */
  async getReadyForConversion(): Promise<ConversionApplicationsResponse> {
    const response = await axiosInstance.get(`${this.conversionUrl}/ready`);
    return response.data;
  }

  /**
   * Get recent conversions
   */
  async getRecentConversions(limit: number = 20): Promise<ConversionApplicationsResponse> {
    const response = await axiosInstance.get(`${this.conversionUrl}/recent`, {
      params: { limit },
    });
    return response.data;
  }
}

// Export singleton instance
export const conversionService = new ConversionService();
export default conversionService;
